import { FC, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Link, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FormattedMessage } from "react-intl";
import InfoIcon from "@mui/icons-material/Info";
import { useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";
import { boxStyle } from "../../../../AppGlobalStyles";
import { graphQlRequest } from "../../../../config/axios/apis/graphql/graphql";
import { taskDefinitionRecordQuery } from "./utils/sysProcessQuery";
import ExportStatusBox from "./utils/ExportStatusBox";
import { ExportStatus, ProcessLogData } from "./utils/utils";
import { BO_CORE_DEFAULT_NUMBER_VALUE, isCancelledError, toastError } from "../../../../utils/AppUtils";
import { Routes } from "../../../../utils/appEnums/AppEnums";
import { downloadFileById } from "../../../../config/axios/apis/bocore/bocore";
import { FileFormatEnum } from "../../../../views/privatePages/ImportAndExport/ImportAndExportTypes";
import { onCloseUnlessEscape } from "../../../../utils/dialogUtils";

interface ExportProgressModalProps {
    openModal: boolean;
    closeModal: () => void;
    sysProcessLogId: number;
}

const ExportProgressModal: FC<ExportProgressModalProps> = ({ openModal, closeModal, sysProcessLogId }) => {
    const navigate = useNavigate();
    const [processLogData, setProcessLogData] = useState<ProcessLogData | null>(null);

    const successValue = useMemo(() => {
        if (sysProcessLogId) {
            if (processLogData) {
                if (processLogData.processedRecords && processLogData.totalRecords && processLogData.totalRecords !== BO_CORE_DEFAULT_NUMBER_VALUE) {
                    return processLogData.processedRecords === processLogData.totalRecords
                        ? Number((processLogData.processedRecords / processLogData.totalRecords) * 100).toFixed(0)
                        : Number((processLogData.processedRecords / processLogData.totalRecords) * 100).toFixed(1);
                }
            }
        }

        return 0;
    }, [processLogData]);

    const handleClose = () => {
        closeModal();
    };

    useEffect(() => {
        const abortController = new AbortController();
        let pollTimeoutId: ReturnType<typeof setTimeout> | undefined;

        const getProcessLogData = () => {
            if (sysProcessLogId) {
                graphQlRequest(taskDefinitionRecordQuery(sysProcessLogId), false, { signal: abortController.signal })
                    .then((response) => {
                        if (response.data.data && response.data.data.TaskDefinitionRecord.records.length > 0) {
                            const record = response.data.data.TaskDefinitionRecord.records[0];
                            const exportedFile = record?.TaskDefinitionRecordTracksExportedFile?.relations[0]?.record;

                            setProcessLogData({
                                status: record.status[0],
                                totalRecords: record.totalRecords,
                                processedRecords: record.processedRecords,
                                exportFileId: exportedFile?.id || null,
                                exportFileName: exportedFile?.name || null,
                                // successRecords: record.successRecords,
                            });

                            // Keep polling until the exported file is linked — it can land slightly after the
                            // status flips to Success. On a failed/cancelled task no file is ever produced, so
                            // stop there instead of polling forever.
                            const status = record.status[0];
                            const hasFailed = status === ExportStatus.FAILURE || status === ExportStatus.FAILED || status === ExportStatus.CANCELLED;

                            if (!hasFailed && !exportedFile?.id) {
                                pollTimeoutId = setTimeout(getProcessLogData, 5000);
                            }
                        }
                    })
                    .catch((error) => {
                        if (isCancelledError(error)) {
                            return;
                        }
                        toastError(error?.response?.data?.message ? error?.response?.data?.message : error?.message);
                    });
            }
        };

        getProcessLogData();

        return () => {
            abortController.abort();
            if (pollTimeoutId) {
                clearTimeout(pollTimeoutId);
            }
        };
    }, [sysProcessLogId]);

    const downloadExportedFile = () => {
        if (processLogData?.exportFileId) {
            downloadFileById(processLogData?.exportFileId).then((res) => {
                saveAs(
                    new Blob([res.data], { type: res.config.headers["Content-Type"] as string }),
                    processLogData.exportFileName || `export-grid.${FileFormatEnum.XLSX.toLowerCase()}`,
                );
            });
        }
    };

    const progressItem =
        processLogData?.totalRecords !== BO_CORE_DEFAULT_NUMBER_VALUE ? (
            <Tooltip
                title={
                    <FormattedMessage
                        id="export.progress-modal-success-processed-records.tooltip.text"
                        defaultMessage="{success} / {totalCount} records successfully processed"
                        values={{ success: processLogData?.processedRecords, totalCount: processLogData?.totalRecords }}
                    />
                }
                sx={{ cursor: "pointer" }}
                arrow
                placement="top"
            >
                <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CircularProgress color="success" value={Number(successValue)} variant="determinate" size="3rem" />
                    <div
                        style={{
                            position: "absolute",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "3rem",
                            height: "3rem",
                        }}
                    >
                        <Typography
                            variant="caption"
                            component="span"
                            color="success"
                            sx={{
                                lineHeight: 1.45,
                            }}
                        >
                            {`${successValue}%`}
                        </Typography>
                    </div>
                </div>
            </Tooltip>
        ) : (
            <Typography variant="h3" sx={{ margin: 0, fontSize: "0.875rem", lineHeight: 1.7, fontWeight: 700, color: "ink.soft" }}>
                <FormattedMessage id="export.label.progress-modal-calculating" defaultMessage="Calculating..." />
            </Typography>
        );

    return (
        <Dialog maxWidth="sm" open={openModal} onClose={onCloseUnlessEscape(handleClose)} fullWidth sx={{ minHeight: "35rem" }}>
            <DialogTitle sx={{ maxWidth: "90%" }}>
                <FormattedMessage id="export.title.progress-modal" defaultMessage="Export Progress" />
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ ...boxStyle }}>
                    <Alert
                        iconMapping={{ info: <InfoIcon fontSize="large" /> }}
                        sx={{ width: "100%", borderRadius: "0.3125rem", alignItems: "center" }}
                        severity="info"
                    >
                        {processLogData?.status === ExportStatus.PENDING ? (
                            <FormattedMessage
                                id="export.progress-modal.info.text-pending-status"
                                defaultMessage="Export started! Processing time may vary depending on the current system load. You can download the exported file from {link} at any time."
                                values={{
                                    link: (
                                        /* eslint-disable-next-line jsx-a11y/anchor-is-valid */ // * false positive - component button renders an actual button instead
                                        <Link
                                            component="button"
                                            variant="inherit"
                                            underline="hover"
                                            onClick={() => navigate(`${Routes.IMPORT_AND_EXPORT}?tab=2`)}
                                            sx={{
                                                color: "text.primary",
                                                fontWeight: "500",
                                                verticalAlign: "baseline",
                                            }}
                                        >
                                            <FormattedMessage id="export.label.progress-modal-link-text" defaultMessage="here" />
                                        </Link>
                                    ),
                                }}
                            />
                        ) : (
                            <FormattedMessage
                                id="export.progress-modal.info.text"
                                defaultMessage="You can download the exported file from {link} at any time or wait for the export to complete and use the Download button."
                                values={{
                                    link: (
                                        /* eslint-disable-next-line jsx-a11y/anchor-is-valid */ // * false positive - component button renders an actual button instead
                                        <Link
                                            component="button"
                                            variant="inherit"
                                            underline="hover"
                                            onClick={() => navigate(`${Routes.IMPORT_AND_EXPORT}?tab=2`)}
                                            sx={{
                                                color: "text.primary",
                                                fontWeight: "500",
                                                verticalAlign: "baseline",
                                            }}
                                        >
                                            <FormattedMessage id="export.label.progress-modal-link-text" defaultMessage="here" />
                                        </Link>
                                    ),
                                }}
                            />
                        )}
                    </Alert>
                </Box>
                <Box sx={{ ...boxStyle, marginTop: "1rem", justifyContent: "space-between" }}>
                    <div style={{ gap: ".5rem", display: "flex", justifyContent: "center", alignItems: "baseline" }}>
                        <Typography variant="h3" sx={{ margin: 0, fontSize: "0.875rem", lineHeight: 1.7, fontWeight: 700, color: "ink.soft" }}>
                            <FormattedMessage id="export.label.progress-modal-export-status" defaultMessage="Export Status" />
                        </Typography>

                        <ExportStatusBox status={processLogData?.status || null} />
                    </div>

                    {processLogData?.status === null || processLogData?.status === ExportStatus.PENDING ? null : progressItem}
                </Box>
                {processLogData?.status === null || processLogData?.status === ExportStatus.PENDING ? null : (
                    <Box sx={{ ...boxStyle, marginTop: "1rem", justifyContent: "flex-end" }}>
                        <Button variant="CTA" disabled={!processLogData?.exportFileId} onClick={downloadExportedFile}>
                            <FormattedMessage id="common.button-label.download" defaultMessage="Download" />
                        </Button>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ position: "absolute", right: "0px", top: ".8%" }}>
                <IconButton onClick={handleClose} aria-label="Close dialog">
                    <CloseIcon sx={(theme) => ({ color: theme.palette.softBlack?.main })} />
                </IconButton>
            </DialogActions>
        </Dialog>
    );
};

export default ExportProgressModal;
